import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(arr) {
  return arr[randInt(0, arr.length - 1)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

const R_CHOICES = [10, 20, 25, 30, 40, 50];
const P_CHOICES = [20, 25, 30, 40, 50, 60, 75];

function generateNumbers() {
  for (let i = 0; i < 200; i += 1) {
    const r = pickOne(R_CHOICES);
    const p = pickOne(P_CHOICES);
    const den = 100 - r;
    const g = gcd(p, den);
    const aStep = den / g;

    const k = randInt(3, 12);
    const A = aStep * k;
    const B = (p * A) / den;
    if (!Number.isInteger(B) || B <= 0) continue;

    const S = A + B;
    if (S < 30 || S > 250) continue;

    return { A, B, S, p, r };
  }

  return { A: 60, B: 40, S: 100, p: 60, r: 40 };
}

export function generateTask() {
  const data = generateNumbers();
  return {
    type: "NestedPercentNumbersTask",
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        Suma dwóch liczb <InlineMath math={"a"} /> i <InlineMath math={"b"} />{" "}
        jest równa <InlineMath math={`${data.S}`} />.
      </p>
      <p>
        Po zmniejszeniu liczby <InlineMath math={"b"} /> o{" "}
        <InlineMath math={`${data.r}\\%`} /> otrzymujemy wartość równą{" "}
        <InlineMath math={`${data.p}\\%`} /> liczby <InlineMath math={"a"} />.
      </p>
      <p>
        Wyznacz liczby <InlineMath math={"a"} /> i <InlineMath math={"b"} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"a ="} />
        <FractionInput
          value={userAnswer?.a || ""}
          onChange={(v) => onChange({ ...userAnswer, a: v })}
          disabled={disabled}
          label="a"
          placeholder="np. 60"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"b ="} />
        <FractionInput
          value={userAnswer?.b || ""}
          onChange={(v) => onChange({ ...userAnswer, b: v })}
          disabled={disabled}
          label="b"
          placeholder="np. 40"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ua = parseVal(userAnswer?.a);
  const ub = parseVal(userAnswer?.b);
  if (!Number.isFinite(ua) || !Number.isFinite(ub)) return { correct: false };

  const okA = Math.abs(ua - data.A) < 0.01;
  const okB = Math.abs(ub - data.B) < 0.01;
  return { correct: okA && okB };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div>
        <InlineMath math={`a = ${data.A}`} />
      </div>
      <div>
        <InlineMath math={`b = ${data.B}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const userAnswer =
    maybeUserAnswer === undefined ? taskOrUserAnswer : maybeUserAnswer;
  const aRaw = userAnswer?.a?.raw || "";
  const bRaw = userAnswer?.b?.raw || "";
  if (!aRaw && !bRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      {aRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(aRaw, "a")} />
        </div>
      ) : (
        <i>(brak a)</i>
      )}
      {bRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(bRaw, "b")} />
        </div>
      ) : (
        <i>(brak b)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { A, B, S, p, r } = data;
  const leftCoeff = 100 - r;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Zapisujemy układ równań z treści:
      </p>
      <BlockMath
        math={String.raw`\begin{cases}a+b=${S}\\\left(1-\frac{${r}}{100}\right)b=\frac{${p}}{100}a\end{cases}`}
      />

      <p>
        <strong>Krok 2:</strong> Usuwamy procenty w drugim równaniu:
      </p>
      <BlockMath
        math={String.raw`\frac{${leftCoeff}}{100}b=\frac{${p}}{100}a`}
      />
      <BlockMath math={`${leftCoeff}b=${p}a`} />

      <p>
        <strong>Krok 3:</strong> Z pierwszego równania{" "}
        <InlineMath math={`a=${S}-b`} />, podstawiamy do drugiego:
      </p>
      <BlockMath math={`${leftCoeff}b=${p}(${S}-b)`} />
      <BlockMath math={`${leftCoeff}b=${p * S}-${p}b`} />
      <BlockMath math={`${leftCoeff + p}b=${p * S}`} />
      <BlockMath math={`b=${B}`} />

      <p>
        <strong>Krok 4:</strong> Liczymy{" "}
        <InlineMath math={`a=${S}-${B}=${A}`} />.
      </p>

      <p>
        <strong>Odpowiedź:</strong> <InlineMath math={`a=${A},\\ b=${B}`} />.
      </p>
    </div>
  );
}
